import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { syllabusData } from '@/mocks/syllabus';
import type { SyllabusTopic, SubTopic } from '@/mocks/syllabus';
import { FilterBar, FilterSearch } from '@/components/feature/FilterBar';

type Lang = 'python' | 'pseudocode' | 'algorithms';

const langConfig: Record<Lang, { label: string; icon: string; color: string; bg: string; border: string; description: string }> = {
  python: {
    label: 'Python Programming',
    icon: 'ri-terminal-box-line',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    description: 'Learn Python from the basics through to algorithms and file handling — everything you need for your GCSE or A Level exam.',
  },
  pseudocode: {
    label: 'Pseudocode',
    icon: 'ri-code-s-slash-line',
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    description: 'Master exam-style pseudocode. Understand the conventions used by AQA, OCR and Edexcel so you can write and trace pseudocode confidently.',
  },
  algorithms: {
    label: 'Algorithms',
    icon: 'ri-flow-chart',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Searching, sorting, graph traversal and complexity — all the algorithms you need to know for GCSE and A Level Computer Science.',
  },
};

// ── Pseudocode topics (no syllabus mock, define inline) ──────────────────────
const pseudocodeTopics: SyllabusTopic[] = [
  {
    id: 'ps-1', unit: 'PS1', title: 'Variables & Assignment',
    icon: 'ri-edit-line', color: 'bg-violet-50', iconColor: 'text-violet-600', borderColor: 'border-violet-200',
    subtopics: [
      { id: 'ps-1-1', title: 'Declaring Variables', description: 'Variables are assigned using the ← symbol in AQA pseudocode. For example: name ← "Alice". No type declaration is needed.', difficulty: 'Foundation', estimatedMins: 15 },
      { id: 'ps-1-2', title: 'Data Types', description: 'Pseudocode supports integers, reals, Booleans, characters and strings. Type conversion functions include INT(), REAL(), STR() and CHR().', difficulty: 'Foundation', estimatedMins: 20 },
      { id: 'ps-1-3', title: 'Constants', description: 'Constants are values that do not change during execution. They are written in uppercase by convention, e.g. MAX_SIZE ← 100.', difficulty: 'Foundation', estimatedMins: 15 },
    ],
  },
  {
    id: 'ps-2', unit: 'PS2', title: 'Input & Output',
    icon: 'ri-terminal-line', color: 'bg-sky-50', iconColor: 'text-sky-600', borderColor: 'border-sky-200',
    subtopics: [
      { id: 'ps-2-1', title: 'USERINPUT', description: 'The USERINPUT keyword reads a value entered by the user. Example: name ← USERINPUT reads a string from the keyboard.', difficulty: 'Foundation', estimatedMins: 10 },
      { id: 'ps-2-2', title: 'OUTPUT', description: 'The OUTPUT keyword displays a value or message. Example: OUTPUT "Hello, " + name displays a greeting. Multiple values can be concatenated with +.', difficulty: 'Foundation', estimatedMins: 10 },
    ],
  },
  {
    id: 'ps-3', unit: 'PS3', title: 'Selection',
    icon: 'ri-git-branch-line', color: 'bg-emerald-50', iconColor: 'text-emerald-600', borderColor: 'border-emerald-200',
    subtopics: [
      { id: 'ps-3-1', title: 'IF … THEN … ELSE', description: 'IF condition THEN … ELSE … END IF. The ELSE branch is optional. Conditions use =, ≠, <, >, ≤, ≥ and logical operators AND, OR, NOT.', difficulty: 'Foundation', estimatedMins: 25 },
      { id: 'ps-3-2', title: 'ELSE IF (nested)', description: 'Multiple conditions are handled with ELSE IF … THEN. Each branch is checked in order; only the first true branch executes.', difficulty: 'Foundation', estimatedMins: 20 },
      { id: 'ps-3-3', title: 'CASE … OF', description: 'CASE variable OF … ENDCASE selects a branch based on the value of a variable. A DEFAULT branch handles any unmatched value.', difficulty: 'Higher', estimatedMins: 25 },
    ],
  },
  {
    id: 'ps-4', unit: 'PS4', title: 'Iteration',
    icon: 'ri-loop-left-line', color: 'bg-orange-50', iconColor: 'text-orange-500', borderColor: 'border-orange-200',
    subtopics: [
      { id: 'ps-4-1', title: 'FOR … TO … END FOR', description: 'FOR i ← start TO end … END FOR repeats a fixed number of times. The loop variable increments by 1 each iteration unless STEP is specified.', difficulty: 'Foundation', estimatedMins: 20 },
      { id: 'ps-4-2', title: 'WHILE … END WHILE', description: 'WHILE condition … END WHILE repeats while the condition is true. The condition is checked before each iteration; the body may never execute.', difficulty: 'Foundation', estimatedMins: 20 },
      { id: 'ps-4-3', title: 'REPEAT … UNTIL', description: 'REPEAT … UNTIL condition executes the body at least once, then checks the condition. The loop stops when the condition becomes true.', difficulty: 'Higher', estimatedMins: 20 },
    ],
  },
  {
    id: 'ps-5', unit: 'PS5', title: 'Subroutines',
    icon: 'ri-function-line', color: 'bg-indigo-50', iconColor: 'text-indigo-600', borderColor: 'border-indigo-200',
    subtopics: [
      { id: 'ps-5-1', title: 'PROCEDURE', description: 'PROCEDURE name(params) … END PROCEDURE defines a subroutine that performs a task without returning a value. Called by writing its name followed by arguments.', difficulty: 'Foundation', estimatedMins: 25 },
      { id: 'ps-5-2', title: 'FUNCTION', description: 'FUNCTION name(params) … RETURN value … END FUNCTION defines a subroutine that returns a result. The return value is used in an expression.', difficulty: 'Higher', estimatedMins: 30 },
      { id: 'ps-5-3', title: 'Parameters & Scope', description: 'Parameters pass data into a subroutine. Local variables exist only inside the subroutine. Global variables are accessible throughout the program.', difficulty: 'Higher', estimatedMins: 25 },
    ],
  },
  {
    id: 'ps-6', unit: 'PS6', title: 'Arrays',
    icon: 'ri-list-check', color: 'bg-rose-50', iconColor: 'text-rose-500', borderColor: 'border-rose-200',
    subtopics: [
      { id: 'ps-6-1', title: '1D Arrays', description: 'A one-dimensional array stores a list of items. Items are accessed by index starting at 0. Example: scores[2] accesses the third element.', difficulty: 'Foundation', estimatedMins: 25 },
      { id: 'ps-6-2', title: '2D Arrays', description: 'A two-dimensional array stores data in rows and columns. Example: grid[row][col] accesses a specific cell. Nested FOR loops are used to traverse 2D arrays.', difficulty: 'Higher', estimatedMins: 30 },
    ],
  },
];

// ── Algorithm topics ─────────────────────────────────────────────────────────
const algorithmTopics: SyllabusTopic[] = [
  {
    id: 'alg-1', unit: 'ALG1', title: 'Searching Algorithms',
    icon: 'ri-search-line', color: 'bg-amber-50', iconColor: 'text-amber-600', borderColor: 'border-amber-200',
    subtopics: [
      { id: 'alg-1-1', title: 'Linear Search', description: 'Checks each item in sequence until the target is found or the list ends. Works on unsorted data. Time complexity: O(n) — slow for large lists.', difficulty: 'Foundation', estimatedMins: 25 },
      { id: 'alg-1-2', title: 'Binary Search', description: 'Compares the target to the middle item and halves the search space each time. Requires sorted data. Time complexity: O(log n) — very fast for large lists.', difficulty: 'Higher', estimatedMins: 35 },
    ],
  },
  {
    id: 'alg-2', unit: 'ALG2', title: 'Sorting Algorithms',
    icon: 'ri-sort-asc', color: 'bg-violet-50', iconColor: 'text-violet-600', borderColor: 'border-violet-200',
    subtopics: [
      { id: 'alg-2-1', title: 'Bubble Sort', description: 'Repeatedly compares adjacent pairs and swaps them if out of order. Simple to implement but slow. Time complexity: O(n²). Best case O(n) if already sorted.', difficulty: 'Foundation', estimatedMins: 30 },
      { id: 'alg-2-2', title: 'Merge Sort', description: 'Divides the list in half recursively, sorts each half, then merges them back together. Time complexity: O(n log n). More efficient than bubble sort for large datasets.', difficulty: 'Higher', estimatedMins: 40 },
      { id: 'alg-2-3', title: 'Insertion Sort', description: 'Builds a sorted section one item at a time by inserting each new item into its correct position. Time complexity: O(n²) worst case, O(n) best case.', difficulty: 'Higher', estimatedMins: 35 },
    ],
  },
  {
    id: 'alg-3', unit: 'ALG3', title: 'Graph & Tree Traversal',
    icon: 'ri-node-tree', color: 'bg-teal-50', iconColor: 'text-teal-600', borderColor: 'border-teal-200',
    subtopics: [
      { id: 'alg-3-1', title: 'Depth-First Search (DFS)', description: 'Explores as far as possible along each branch before backtracking. Uses a stack (or recursion). Used for maze solving and topological sorting.', difficulty: 'Higher', estimatedMins: 40 },
      { id: 'alg-3-2', title: 'Breadth-First Search (BFS)', description: 'Explores all neighbours at the current depth before moving deeper. Uses a queue. Finds the shortest path in an unweighted graph.', difficulty: 'Higher', estimatedMins: 40 },
      { id: 'alg-3-3', title: 'Tree Traversal (In/Pre/Post-order)', description: 'In-order visits left, root, right. Pre-order visits root, left, right. Post-order visits left, right, root. Each order has different applications in expression evaluation and file systems.', difficulty: 'Higher', estimatedMins: 35 },
    ],
  },
  {
    id: 'alg-4', unit: 'ALG4', title: "Dijkstra's Algorithm",
    icon: 'ri-route-line', color: 'bg-sky-50', iconColor: 'text-sky-600', borderColor: 'border-sky-200',
    subtopics: [
      { id: 'alg-4-1', title: 'Shortest Path Concept', description: "Dijkstra's algorithm finds the shortest path from a source node to all other nodes in a weighted graph. It always processes the nearest unvisited node next.", difficulty: 'Higher', estimatedMins: 45 },
      { id: 'alg-4-2', title: 'Step-by-Step Trace', description: 'Initialise all distances to infinity except the source (0). Update distances to neighbours. Mark the current node as visited. Repeat until all nodes are visited.', difficulty: 'Higher', estimatedMins: 50 },
    ],
  },
  {
    id: 'alg-5', unit: 'ALG5', title: 'Complexity & Big O Notation',
    icon: 'ri-bar-chart-line', color: 'bg-rose-50', iconColor: 'text-rose-500', borderColor: 'border-rose-200',
    subtopics: [
      { id: 'alg-5-1', title: 'Time Complexity', description: 'Big O notation describes how an algorithm\'s running time grows with input size. O(1) constant, O(log n) logarithmic, O(n) linear, O(n log n) linearithmic, O(n²) quadratic.', difficulty: 'Higher', estimatedMins: 40 },
      { id: 'alg-5-2', title: 'Space Complexity', description: 'Space complexity measures how much memory an algorithm uses relative to input size. An in-place algorithm uses O(1) extra space; merge sort uses O(n) extra space.', difficulty: 'Higher', estimatedMins: 30 },
      { id: 'alg-5-3', title: 'Comparing Algorithms', description: 'Choosing between algorithms involves trading off time complexity, space complexity, simplicity and whether the data is already partially sorted. No single algorithm is best in all cases.', difficulty: 'Higher', estimatedMins: 25 },
    ],
  },
];

function TopicCard({ topic, searchQuery }: { topic: SyllabusTopic; searchQuery: string }) {
  const [expandedSub, setExpandedSub] = useState<string | null>(null);

  const filteredSubs = searchQuery.trim()
    ? topic.subtopics.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : topic.subtopics;

  if (filteredSubs.length === 0) return null;

  return (
    <div className={`bg-white rounded-xl border ${topic.borderColor} overflow-hidden`}>
      <div className={`flex items-center gap-3 px-5 py-4 ${topic.color} border-b ${topic.borderColor}`}>
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
          <i className={`${topic.icon} ${topic.iconColor} text-base`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <span className={`text-xs font-bold uppercase tracking-wider ${topic.iconColor} opacity-70`}>{topic.unit}</span>
          <h3 className="text-sm font-bold text-slate-800 leading-tight">{topic.title}</h3>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white/60 px-2 py-0.5 rounded-full flex-shrink-0">
          {filteredSubs.length} subtopics
        </span>
      </div>
      <ul className="divide-y divide-slate-50">
        {filteredSubs.map((sub: SubTopic) => (
          <li key={sub.id}>
            <button
              onClick={() => setExpandedSub(expandedSub === sub.id ? null : sub.id)}
              className="w-full text-left px-5 py-3 group cursor-pointer hover:bg-slate-50 transition-colors flex items-start gap-3"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`ri-arrow-right-s-line text-slate-300 text-sm group-hover:text-slate-500 transition-all ${expandedSub === sub.id ? 'rotate-90' : ''}`}></i>
              </div>
              <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors leading-snug">{sub.title}</span>
            </button>
            {expandedSub === sub.id && (
              <div className="px-5 pb-4 ml-7">
                <p className="text-xs text-slate-500 leading-relaxed mb-3">{sub.description}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                    sub.difficulty === 'Foundation' ? 'bg-emerald-100 text-emerald-700' :
                    sub.difficulty === 'Higher' ? 'bg-orange-100 text-orange-700' :
                    'bg-sky-100 text-sky-700'
                  }`}>{sub.difficulty}</span>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i>{sub.estimatedMins} min
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Embedded online compiler URLs ────────────────────────────────────────────
const compilerUrls: Record<Lang, string> = {
  python: 'https://trinket.io/embed/python3',
  pseudocode: 'https://www.onlinegdb.com/online_python_compiler#',
  algorithms: 'https://trinket.io/embed/python3',
};

export default function ProgrammingPage() {
  const [searchParams] = useSearchParams();
  const initialLang = (searchParams.get('lang') as Lang) || 'python';
  const [selectedLang, setSelectedLang] = useState<Lang>(initialLang);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompiler, setShowCompiler] = useState(false);

  const config = langConfig[selectedLang];

  const pythonSyllabus = useMemo(
    () => syllabusData.find((s) => s.subject === 'Python'),
    []
  );

  const topics: SyllabusTopic[] = useMemo(() => {
    if (selectedLang === 'python') return pythonSyllabus?.topics ?? [];
    if (selectedLang === 'pseudocode') return pseudocodeTopics;
    return algorithmTopics;
  }, [selectedLang, pythonSyllabus]);

  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return topics;
    return topics.filter(
      (t) =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.subtopics.some(
          (s) =>
            s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );
  }, [topics, searchQuery]);

  const handleLangChange = (lang: Lang) => {
    setSelectedLang(lang);
    setSearchQuery('');
    setShowCompiler(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20glowing%20code%20terminal%20and%20programming%20syntax%2C%20deep%20navy%20gradient%2C%20green%20terminal%20text%20patterns%2C%20futuristic%20coding%20aesthetic%2C%20clean%20minimal%20design%2C%20emerald%20and%20teal%20accents%20on%20dark%20background&width=1440&height=320&seq=programming1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Programming Resources</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            Programming Resources
          </h1>
          <p className="text-slate-400 text-base max-w-2xl mb-6">
            Python, pseudocode and algorithms — all the programming content you need for GCSE and A Level Computer Science, with topics, subtopics and a live code editor to practise.
          </p>

          {/* Lang tabs */}
          <div className="flex flex-wrap gap-2">
            {(Object.entries(langConfig) as [Lang, typeof langConfig[Lang]][]).map(([lang, cfg]) => (
              <button
                key={lang}
                onClick={() => handleLangChange(lang)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                  selectedLang === lang
                    ? 'bg-white text-slate-900 border-white'
                    : 'bg-slate-800/60 text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <i className={`${cfg.icon} text-sm`}></i>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Description banner */}
      <div className={`${config.bg} border-b ${config.border}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
              <i className={`${config.icon} ${config.color} text-base`}></i>
            </div>
            <p className={`text-sm font-medium ${config.color}`}>{config.description}</p>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <FilterBar>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <i className="ri-book-open-line text-slate-400"></i>
          <span className="font-semibold text-slate-700">{filteredTopics.length}</span> units &middot;
          <span className="font-semibold text-slate-700">{filteredTopics.reduce((a, t) => a + t.subtopics.length, 0)}</span> subtopics
        </div>
        <div className="sm:ml-auto flex items-center gap-3">
          <FilterSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search topics or subtopics..."
            width="sm:w-72"
          />
          <button
            onClick={() => setShowCompiler(true)}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-sm"></i>
            Try Coding Here!
          </button>
        </div>
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">

        {/* Topics Grid */}
        {filteredTopics.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No topics found</p>
            <p className="text-slate-400 text-sm">Try a different search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTopics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} searchQuery={searchQuery} />
            ))}
          </div>
        )}

        {/* Try Coding Section */}
        <div className="mt-14">
          <div className="rounded-2xl overflow-hidden border border-slate-200">
            {/* Section header */}
            <div className="bg-emerald-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-code-s-slash-line text-white text-base"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Try Coding Here!
                  </h2>
                  <p className="text-emerald-100 text-xs">Write and run Python code directly in your browser — no installation needed</p>
                </div>
              </div>
              {!showCompiler && (
                <button
                  onClick={() => setShowCompiler(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg text-sm font-bold hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-sm"></i>
                  Launch Editor
                </button>
              )}
            </div>

            {/* Compiler embed */}
            {showCompiler ? (
              <div className="bg-slate-900">
                <iframe
                  src={compilerUrls[selectedLang]}
                  title="Online Python Compiler"
                  className="w-full"
                  style={{ height: '520px', border: 'none' }}
                  allow="clipboard-write"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              </div>
            ) : (
              <div
                className="bg-slate-900 flex flex-col items-center justify-center cursor-pointer group"
                style={{ height: '280px' }}
                onClick={() => setShowCompiler(true)}
              >
                {/* Fake terminal preview */}
                <div className="w-full max-w-xl px-8">
                  <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700">
                    {/* Terminal bar */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-700/60 border-b border-slate-700">
                      <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                      <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                      <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                      <span className="ml-3 text-xs text-slate-400 font-mono">main.py</span>
                    </div>
                    {/* Fake code lines */}
                    <div className="px-5 py-4 font-mono text-xs space-y-1.5">
                      <p><span className="text-slate-500">1</span><span className="ml-4 text-slate-400"># Write your Python code here</span></p>
                      <p><span className="text-slate-500">2</span><span className="ml-4 text-violet-400">print</span><span className="text-slate-300">(</span><span className="text-emerald-400">"Hello, World!"</span><span className="text-slate-300">)</span></p>
                      <p><span className="text-slate-500">3</span><span className="ml-4 text-sky-400">name</span><span className="text-slate-300"> = </span><span className="text-violet-400">input</span><span className="text-slate-300">(</span><span className="text-emerald-400">"Enter your name: "</span><span className="text-slate-300">)</span></p>
                      <p><span className="text-slate-500">4</span><span className="ml-4 text-violet-400">print</span><span className="text-slate-300">(</span><span className="text-emerald-400">"Hello, "</span><span className="text-slate-300"> + name)</span></p>
                      <p><span className="text-slate-500">5</span></p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-2 text-slate-400 group-hover:text-emerald-400 transition-colors">
                  <i className="ri-play-circle-line text-xl"></i>
                  <span className="text-sm font-medium">Click to launch the code editor</span>
                </div>
              </div>
            )}
          </div>

          {/* Tips row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
            {[
              { icon: 'ri-lightbulb-line', title: 'Practise as you learn', desc: 'Type out each example yourself rather than copying — it helps the syntax stick.' },
              { icon: 'ri-bug-line', title: 'Make mistakes on purpose', desc: 'Introduce errors and see what happens. Understanding error messages is a key exam skill.' },
              { icon: 'ri-file-text-line', title: 'Recreate past paper tasks', desc: 'Find a programming question from a past paper and solve it here before checking the mark scheme.' },
            ].map((tip) => (
              <div key={tip.title} className="flex items-start gap-3 bg-white rounded-xl border border-slate-100 p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
                  <i className={`${tip.icon} text-emerald-600 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-0.5">{tip.title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
